import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";

const successionSteps = [
  {
    number: "1",
    title: "Diagnostic",
    description: "Visite du logement, écoute des souhaits des héritiers. Nous identifions avec vous les objets à conserver, à classer, à mettre en carton ou à recycler. Nous évaluons ensemble l'état du bien, et vous proposons un plan d'intervention qui inclut la stratégie idéale pour vous afin de parvenir à la mise en vente dans les meilleures conditions possibles."
  },
  {
    number: "2",
    title: "Tri accompagné",
    description: "Nous organisons un tri méthodique selon vos instructions et en coordination étroite avec votre famille incluant : la séparation des objets à conserver, à vendre, à donner ou à recycler incluant la possibilité d'expédition vers les membres de la famille, des associations caritatives ou des espaces de stockage."
  },
  {
    number: "3",
    title: "Valorisation des objets/du mobilier",
    description: "Si un expert n'a pas été mandaté par le notaire et les héritiers et qu'une évaluation forfaitaire (5%) a été décidée, nous pouvons accompagner la famille pour la sélection de mobilier et d'objets à faire expertiser et/ou à proposer à des antiquaires, brocanteurs. Nous ne sommes absolument pas liés à des prestataires, nous pouvons passer par les vôtres ou gérer un appel d'offres.\nSi un expert a été nommé, nous pouvons coordonner les interlocuteurs pour la vente des objets et du mobilier."
  },
  {
    number: "4",
    title: "Remise en état du bien",
    description: "Nous décidons avec vous si le bien sera plus attractif, meublé ou vide. L'objectif étant de mettre l'appartement/la maison en valeur en vue de sa commercialisation.\nNous organisons l'aménagement du bien, si besoin afin d'en optimiser la perception et l'attractivité. Rangement soigneux, nettoyage complet et petits travaux si nécessaire."
  },
  {
    number: "5",
    title: "Mise en vente, stratégie de commercialisation",
    description: "À cette étape nous nous chargeons de toutes les démarches obligatoires avant la mise en vente : les diagnostics à réaliser, les photos du bien, l'étude des rapports d'assemblée, la communication avec le syndic de l'immeuble pour obtenir les documents nécessaires.\nNous réalisons l'annonce à faire paraître sur les sites appropriés et organisons des visites jusqu'à la vente."
  }
];

const ImagePlaceholder = ({ testId, aspect = "aspect-[16/9]", src, alt }) => (
  <div
    data-testid={testId}
    className={`relative ${aspect} overflow-hidden`}
  >
    {src ? (
      <img src={src} alt={alt || "Emplacement-immo"} className="w-full h-full object-cover" />
    ) : (
      <div className="w-full h-full bg-surface border border-line flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-px bg-gold mx-auto mb-3"></div>
          <p className="font-serif text-text-muted text-base italic">Image à uploader</p>
        </div>
      </div>
    )}
  </div>
);

export const AutresServicesPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div data-testid="valorisation-page" className="pt-32">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold text-sm font-semibold uppercase tracking-[0.3em] mb-6">
              Services de valorisation
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-8">
              Expert en valorisation
            </h1>
            <p className="text-text-secondary text-lg">
              Valoriser votre bien, c'est l'assurance de le commercialiser au meilleur prix.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Image - hero secondary */}
      <section className="py-0 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
          <ImagePlaceholder
            testId="valorisation-hero-placeholder"
            aspect="h-[450px]"
            src="https://res.cloudinary.com/dpeldfqpi/image/upload/v1784554296/iStock-489149890_qcr2z6.jpg"
            alt="Valorisation immobilière Paris"
          />
        </div>
      </section>

      {/* Pourquoi ces prestations */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white border border-gold/30 p-8 md:p-14"
          >
            <h2 className="font-serif text-text-primary text-3xl md:text-4xl mb-8">
              Pourquoi proposons-nous ces prestations ?
            </h2>
            <div className="space-y-6 text-text-secondary leading-relaxed">
              <p>
                Au fil de notre expérience dans l'immobilier, nous avons constaté à quel point certaines étapes de vie peuvent rendre difficile la mise en valeur d'un bien. Une fin de location, un projet de location saisonnière type Airbnb, une succession ou simplement l'envie de redonner vie à un appartement ou une maison… autant de situations où il est parfois compliqué de savoir par où commencer.
              </p>
              <p>
                Nous l'avons observé autour de nous, mais aussi vécu à titre personnel. Et nous avons compris qu'au-delà de l'immobilier, il y avait un véritable besoin d'accompagnement humain, de regard professionnel et de conseils concrets pour révéler tout le potentiel d'un bien.
              </p>
              <p>
                C'est ainsi qu'est née notre prestation de valorisation immobilière, en complément naturel de notre métier de chasseur d'appartements et d'agent immobilier.
              </p>
              <p className="text-gold font-semibold">
                Nous abordons chaque projet avec la même énergie, la même motivation et surtout la même empathie que celle qui guide chacune de nos missions. Parce qu'un bien immobilier raconte toujours une histoire, notre rôle est de lui permettre de commencer un nouveau chapitre dans les meilleures conditions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section data-testid="main-services-section" className="py-20 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Service 1 - Révéler le potentiel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="main-service-0"
              className="bg-white border border-line p-10 hover:border-gold transition-colors duration-300 shadow-sm"
            >
              <div className="flex items-baseline gap-5 mb-6">
                <span className="font-serif text-gold text-5xl leading-none">1</span>
                <h3 className="font-serif text-text-primary text-2xl">
                  Révéler le potentiel d'un bien
                </h3>
              </div>
              <div className="space-y-4 text-text-secondary leading-relaxed">
                <p>
                  Que vous ayez besoin de louer un bien sur Airbnb, de le remettre en état entre deux locations ou de le vendre : Nous prenons en charge la mise en valeur de votre bien pour en révéler pleinement le potentiel et en optimiser la perception et donc le prix.
                </p>
                <p>
                  Du désencombrement aux ajustements décoratifs, en passant par le nettoyage, les petites réparations ou la sélection d'éléments décoratifs adaptés, chaque intervention est pensée avec précision, en fonction du bien et de votre budget.
                </p>
                <p>
                  Nous coordonnons une équipe dédiée, rigoureusement sélectionnée, et assurons un suivi direct pour garantir une exécution irréprochable.
                </p>
              </div>
            </motion.div>

            {/* Service 2 - Succession Sérénité */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="main-service-1"
              className="bg-white border border-line p-10 hover:border-gold transition-colors duration-300 shadow-sm"
            >
              <div className="flex items-baseline gap-5 mb-6">
                <span className="font-serif text-gold text-5xl leading-none">2</span>
                <h3 className="font-serif text-text-primary text-2xl">
                  Succession Sérénité
                </h3>
              </div>
              <div className="space-y-4 text-text-secondary leading-relaxed">
                <p>
                  Lors d'une succession, vous avez déjà beaucoup à gérer, sur le plan personnel, familial et souvent émotionnel. Le temps manque, et certaines démarches, bien que nécessaires, peuvent être difficiles à assumer.
                </p>
                <p>
                  Nous vous proposons une prise en charge complète et attentive, conçue pour vous décharger concrètement. En lien étroit avec vous et dans le respect de chaque situation, nous prenons en main l'ensemble des étapes : tri des effets personnels, organisation du débarras, nettoyage approfondi, remise en état du bien et mise en valeur en vue de sa vente.
                </p>
                <p>
                  Chaque intervention est menée avec discrétion, respect et délicatesse, en tenant compte de l'histoire des lieux et des souvenirs qui y sont attachés.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Succession Engagement */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white border border-gold/30 p-8 md:p-14 text-center"
          >
            <p className="text-text-secondary leading-relaxed mb-6">
              Nous coordonnons l'ensemble des intervenants en votre nom et pilotons chaque étape avec exigence, afin que vous n'ayez rien à gérer.
            </p>
            <p className="text-text-secondary leading-relaxed mb-6">
              En nous confiant cette mission, vous vous libérez du temps et de la complexité, tout en vous assurant que le bien sera présenté dans les meilleures conditions, un élément déterminant pour vendre rapidement et au meilleur prix, sans avoir à le brader.
            </p>
            <p className="text-gold font-semibold">
              Notre engagement : vous permettre de traverser cette étape en étant accompagné, pour que votre bien soit prêt à être commercialisé aux meilleures conditions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Image before steps */}
      <section className="py-0 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <ImagePlaceholder
            testId="valorisation-steps-placeholder"
            aspect="h-[450px]"
            src="https://res.cloudinary.com/dpeldfqpi/image/upload/v1784553733/istock_serenite%CC%81_obvr2n.jpg"
            alt="Succession Sérénité"
          />
        </div>
      </section>

      {/* Succession Steps */}
      <section data-testid="succession-section" className="py-20 md:py-28 bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-text-primary text-center mb-20"
          >
            Les étapes de la valorisation Succession Sérénité
          </motion.h2>

          <div className="space-y-8">
            {successionSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`succession-step-${index}`}
                className="bg-white border border-line p-10 shadow-sm"
              >
                <div className="flex items-baseline gap-5 mb-4">
                  <span className="font-serif text-gold text-4xl leading-none">{step.number}</span>
                  <h3 className="font-serif text-text-primary text-xl">
                    {step.title}
                  </h3>
                </div>
                <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl text-text-primary mb-8">
              Besoin d'un service de valorisation ?
            </h2>
            <Link to="/contact">
              <Button
                data-testid="cta-services-contact"
                className="bg-navy text-white hover:bg-navy hover:border-gold border border-navy font-semibold px-10 py-6 text-lg rounded-none"
              >
                Nous contacter
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
